'use client';

import { useState } from 'react';
import { Search, SlidersHorizontal, ExternalLink, Link, Image, Sparkles, Globe } from 'lucide-react';
import type { Post, SearchFilters } from '@/types';
import { searchBM25, searchSimilar } from '@/lib/api';
import { formatTimestamp, getBlueskyPostUrl, truncateText } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';

// Parse text and convert URLs, @mentions, and #hashtags to clickable links
function parseTextWithLinks(text: string): React.ReactNode[] {
  // Combined regex for URLs, @mentions, and #hashtags
  const combinedRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)|(@[\w.-]+)|(\#[\w\u00C0-\u024F]+)/g;
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = combinedRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    const [fullMatch] = match;
    const key = `${match.index}-${fullMatch}`;

    if (fullMatch.startsWith('http')) {
      // URL
      result.push(
        <a
          key={key}
          href={fullMatch}
          target="_blank"
          rel="noopener noreferrer"
          className="text-main hover:underline break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {fullMatch}
        </a>
      );
    } else if (fullMatch.startsWith('@')) {
      // @mention - link to Bluesky profile
      const handle = fullMatch.slice(1);
      result.push(
        <a
          key={key}
          href={`https://bsky.app/profile/${handle}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-main hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {fullMatch}
        </a>
      );
    } else if (fullMatch.startsWith('#')) {
      // #hashtag - link to Bluesky search
      const tag = fullMatch.slice(1);
      result.push(
        <a
          key={key}
          href={`https://bsky.app/search?q=%23${encodeURIComponent(tag)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-main hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {fullMatch}
        </a>
      );
    }

    lastIndex = match.index + fullMatch.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result.length > 0 ? result : [text];
}

export function SearchPanel() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [useHybrid, setUseHybrid] = useState(false);

  // Filter state
  const [hasLink, setHasLink] = useState<string>('any');
  const [hasMedia, setHasMedia] = useState<string>('any');
  const [language, setLanguage] = useState('any');
  const [minLength, setMinLength] = useState<string>('');
  const [timeRange, setTimeRange] = useState('any');
  const [limit, setLimit] = useState('20');
  const [sortBy, setSortBy] = useState('relevance');

  const handleExampleClick = (exampleQuery: string) => {
    setQuery(exampleQuery);
    // Trigger search after setting query
    setTimeout(() => {
      handleSearchWithQuery(exampleQuery);
    }, 0);
  };

  const handleSearchWithQuery = async (searchQuery: string) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const filters: SearchFilters = {};

      if (hasLink !== 'any') filters.has_link = hasLink === 'true';
      if (hasMedia !== 'any') filters.has_media = hasMedia === 'true';
      if (language !== 'any') filters.langs = [language];
      if (minLength) filters.min_length = Number(minLength);

      if (timeRange !== 'any') {
        const now = new Date();
        let start: Date | undefined;

        switch (timeRange) {
          case '1h':
            start = new Date(now.getTime() - 60 * 60 * 1000);
            break;
          case '24h':
            start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case '7d':
            start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30d':
            start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
        }

        if (start) {
          filters.time_range = { start: start.toISOString() };
        }
      }

      const limitNum = Number(limit);
      const preferRecent = sortBy === 'recent';

      const executeHybrid = async () =>
        searchSimilar({ text: searchQuery, filters, limit: limitNum, hybrid: true, prefer_recent: preferRecent });
      const executeBM25 = async () => searchBM25({ text: searchQuery, filters, limit: limitNum, prefer_recent: preferRecent });

      const response = useHybrid ? await executeHybrid() : await executeBM25();
      setResults(response.posts);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Search failed';
      if (useHybrid) {
        // Fallback to BM25 if hybrid fails or times out so the UI doesn't stay stuck.
        try {
          const limitNum = Number(limit);
          const preferRecent = sortBy === 'recent';
          const filters: SearchFilters = {};
          if (hasLink !== 'any') filters.has_link = hasLink === 'true';
          if (hasMedia !== 'any') filters.has_media = hasMedia === 'true';
          if (language !== 'any') filters.langs = [language];
          if (minLength) filters.min_length = Number(minLength);
          if (timeRange !== 'any') {
            const now = new Date();
            let start: Date | undefined;
            switch (timeRange) {
              case '1h':
                start = new Date(now.getTime() - 60 * 60 * 1000);
                break;
              case '24h':
                start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                break;
              case '7d':
                start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
              case '30d':
                start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                break;
            }
            if (start) {
              filters.time_range = { start: start.toISOString() };
            }
          }
          const bm25 = await searchBM25({ text: searchQuery, filters, limit: limitNum, prefer_recent: preferRecent });
          setResults(bm25.posts);
          setError(`Hybrid failed (${message}); showing keyword results instead.`);
        } catch (fallbackErr) {
          const fallbackMsg = fallbackErr instanceof Error ? fallbackErr.message : 'fallback failed';
          setError(`Hybrid failed (${message}); keyword fallback failed (${fallbackMsg}).`);
        }
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => handleSearchWithQuery(query);

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b-2 border-border bg-secondary-background">
        <h2 className="text-lg font-heading text-foreground mb-1">
          {useHybrid ? 'Hybrid Search' : 'Keyword Search'}
        </h2>
        <p className="text-sm font-base text-foreground/70 mb-4">
          {useHybrid
            ? 'Blend semantic similarity with BM25 keyword matching'
            : 'Find posts using BM25 full-text search'}
        </p>

        <div className="space-y-4">
          <Textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter keywords to search... e.g., 'bluesky social media'"
            className="h-24 resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.metaKey) {
                handleSearch();
              }
            }}
          />

          <div className="flex items-center gap-3">
            <Button onClick={handleSearch} disabled={loading || !query.trim()}>
              <Search className="w-4 h-4" />
              {loading ? 'Searching...' : 'Search'}
            </Button>

            <Button
              variant={showFilters ? 'default' : 'neutral'}
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </Button>

            <Button
              variant={useHybrid ? 'default' : 'neutral'}
              onClick={() => setUseHybrid((prev) => !prev)}
            >
              <Sparkles className="w-4 h-4" />
              Hybrid
            </Button>

            {results.length > 0 && (
              <Badge variant="neutral">{results.length} results</Badge>
            )}
          </div>

          {showFilters && (
            <div className="p-4 bg-background rounded-base border-2 border-border grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-heading text-foreground mb-2">
                  Time range
                </label>
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any time</SelectItem>
                    <SelectItem value="1h">Last hour</SelectItem>
                    <SelectItem value="24h">Last 24 hours</SelectItem>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-heading text-foreground mb-2">
                  Language
                </label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="pt">Portuguese</SelectItem>
                    <SelectItem value="ja">Japanese</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-heading text-foreground mb-2">
                  Results
                </label>
                <Select value={limit} onValueChange={setLimit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-heading text-foreground mb-2">
                  Sort
                </label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">Relevance</SelectItem>
                    <SelectItem value="recent">Most recent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-heading text-foreground mb-2">
                  Has link
                </label>
                <Select value={hasLink} onValueChange={setHasLink}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-heading text-foreground mb-2">
                  Has media
                </label>
                <Select value={hasMedia} onValueChange={setHasMedia}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-heading text-foreground mb-2">
                  Min length
                </label>
                <Input
                  type="number"
                  value={minLength}
                  onChange={(e) => setMinLength(e.target.value)}
                  placeholder="chars"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="m-4 p-4 bg-red-100 border-2 border-border rounded-base text-red-800 font-base">
            {error}
          </div>
        )}


        <div className="divide-y-2 divide-border">
          {results.map((post) => (
            <div key={post.uri} className="p-4 hover:bg-main/10 transition-colors">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-heading text-foreground text-sm truncate">
                      @{post.handle || truncateText(post.repo_did, 16)}
                    </span>
                    <span className="text-foreground/50 text-xs">·</span>
                    <span className="text-foreground/50 text-xs font-base">
                      {formatTimestamp(post.created_at)}
                    </span>
                    {post.score !== undefined && (
                      <Badge variant="default" className="text-xs">
                        {(post.score * 100).toFixed(1)}% match
                      </Badge>
                    )}
                  </div>
                  <p className="text-foreground text-sm font-base whitespace-pre-wrap break-words">
                    {parseTextWithLinks(post.text)}
                  </p>

                  {/* Link domains preview */}
                  {post.link_domains && post.link_domains.length > 0 && (
                    <div className="mt-2 p-2 bg-background rounded-base border-2 border-border">
                      <div className="flex items-center gap-2 text-sm">
                        <Globe className="w-4 h-4 text-foreground/50 flex-shrink-0" />
                        <div className="flex flex-wrap gap-1">
                          {post.link_domains.map((domain, i) => (
                            <a
                              key={i}
                              href={`https://${domain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-main hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {domain}
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Images */}
                  {post.image_urls && post.image_urls.length > 0 && (
                    <div className={`mt-2 grid gap-2 ${post.image_urls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      {post.image_urls.map((url, i) => (
                        <a
                          key={i}
                          href={getBlueskyPostUrl(post.uri)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block rounded-base border-2 border-border overflow-hidden hover:border-main transition-colors"
                        >
                          <img
                            src={url}
                            alt=""
                            className="w-full h-auto max-h-64 object-cover"
                            loading="lazy"
                          />
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Language badges */}
                  {post.langs && post.langs.length > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="neutral" className="text-xs">
                        {post.langs.join(', ')}
                      </Badge>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleExampleClick(post.text)}
                    className="text-foreground/50 hover:text-main transition-colors"
                    title="Find similar posts"
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>
                  <a
                    href={getBlueskyPostUrl(post.uri)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground/50 hover:text-main transition-colors"
                    title="Open on Bluesky"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
